/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import verificationService from '../../services/verificationService';
import { useVendorVerification } from '../../hooks/useVendorVerification';
import {
 FileText,
 Building,
 MapPin,
 CheckCircle,
 Loader2,
 ShieldCheck,
 Clock,
 LogIn,
} from 'lucide-react';
import Button from '../../components/shared/Button';
import { useToastContext } from '../../contexts/ToastContext';
import { useServiceCategories } from '../../services/serviceCategoryService';
import useOperatingMarkets from '../../hooks/useOperatingMarkets';
import { countryRequiresPostalCode } from '../../utils/countryPostal';
import { isConfiguredMarket } from '../../utils/marketOptions';
import useGeolocation from '../../hooks/useGeolocation';
import Step1BusinessInfo from '../../components/vendor/verification/Step1BusinessInfo';
import Step2ContactLocation from '../../components/vendor/verification/Step2ContactLocation';
import Step3Documents from '../../components/vendor/verification/Step3Documents';
import Step4Review from '../../components/vendor/verification/Step4Review';
import { useQuery } from '@tanstack/react-query';
import { siteSettingsService } from '../../services/siteSettingsService';
import {
 clearVerificationDraft,
 isAuthError,
 isSessionExpired,
 loadVerificationDraft,
 markVerificationDraftTimedOut,
 saveVerificationDraft,
} from '../../utils/authSession';

const VerificationShell = ({ children, wide = false }) => (
 <div className="min-h-screen bg-surface flex flex-col">
 <header className="flex h-16 items-center justify-center overflow-hidden bg-black">
 <Link to="/" className="flex items-center justify-center">
 <img src="/images/VendaLOGO1.png" alt="Vendalist" className="h-16 w-auto" />
 </Link>
 </header>
 <main className="flex flex-1 justify-center px-4 py-10">
 <div className={`w-full ${wide ? 'max-w-xl' : 'max-w-md'}`}>{children}</div>
 </main>
 </div>
);

const StatusCard = ({ icon: Icon, iconClass, title, body, footer, action }) => (
 <VerificationShell>
 <div className="text-center">
 <div
 className={`mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full border ${iconClass}`}
 >
 <Icon className="h-7 w-7" />
 </div>
 <h1 className="text-2xl font-semibold text-gray-900">{title}</h1>
 <p className="mt-2 text-sm text-gray-500 leading-relaxed">{body}</p>
 {footer ? <p className="mt-3 text-xs text-gray-400">{footer}</p> : null}
 {action || null}
 </div>
 </VerificationShell>
);

const VerificationSkeleton = () => (
 <VerificationShell wide>
 <div className="animate-pulse space-y-6">
 <div className="space-y-2">
 <div className="mx-auto h-7 w-48 rounded bg-gray-200" />
 <div className="mx-auto h-4 w-64 max-w-full rounded bg-gray-100" />
 </div>
 <div className="h-1 w-full rounded-full bg-gray-100" />
 <div className="space-y-5">
 {[1, 2, 3, 4].map((i) => (
 <div key={i} className="space-y-2">
 <div className="h-3 w-28 rounded bg-gray-200" />
 <div className="h-11 w-full rounded-xl bg-gray-100" />
 </div>
 ))}
 </div>
 </div>
 </VerificationShell>
);

const VendorVerification = () => {
 const { user, logout } = useAuth();
 const navigate = useNavigate();
 const { isVendorVerified, isVerificationPending, isLoading, refreshVerificationStatus } =
 useVendorVerification();
 const [currentStep, setCurrentStep] = useState(1);
 const [isSubmitting, setIsSubmitting] = useState(false);
 const [sessionTimedOut, setSessionTimedOut] = useState(false);
 const { success, error: showError, info } = useToastContext();
 const hasNavigatedRef = useRef(false);
 const justSubmittedRef = useRef(false);
 const draftRestoredRef = useRef(false);
 const {
 country: geoCountry,
 countryCode: geoCountryCode,
 city: geoCity,
 state: geoState,
 loading: geoLoading,
 getCurrentLocation,
 } = useGeolocation();

 useEffect(() => {
 if (!isLoading && isVendorVerified && !hasNavigatedRef.current) {
 hasNavigatedRef.current = true;
 success('You have already completed verification!', 'success');
 navigate('/vendor');
 }
 }, [isLoading, isVendorVerified, navigate, success]);

 useEffect(() => {
 if (!isLoading && isVerificationPending && !hasNavigatedRef.current) {
 hasNavigatedRef.current = true;

 if (!justSubmittedRef.current) {
 info(
 'Verification Pending',
 'Your verification is currently pending review. Please wait for admin approval.'
 );
 }

 navigate('/vendor');
 }
 }, [isLoading, isVerificationPending, navigate, info]);

 const {
 data: categoriesData,
 isLoading: categoriesLoading,
 error: categoriesError,
 } = useServiceCategories({ active: true });

 const serviceCategories = categoriesData?.data?.data || [];

 const {
 markets,
 options: marketOptions,
 isLoading: marketsLoading,
 error: marketsError,
 } = useOperatingMarkets({ activeOnly: true });

 const [formData, setFormData] = useState({
 business_name: '',
 service_categories: null,
 business_description: '',
 years_experience: '',
 specialties: [],
 business_phone: '',
 business_email: '',
 website: '',
 social_media: {},
 address: '',
 city: '',
 state: '',
 zip_code: '',
 country: '',
 business_license: null,
 insurance_certificate: null,
 id_document: null,
 insurance_info: '',
 business_hours: {
 monday: { available: true },
 tuesday: { available: true },
 wednesday: { available: true },
 thursday: { available: true },
 friday: { available: true },
 saturday: { available: true },
 sunday: { available: true },
 },
 team_size: '',
 certifications: '',
 references: '',
 });

 const [errors, setErrors] = useState({});
 const [uploadProgress, setUploadProgress] = useState({});
 const [selectedOptionalPlatforms, setSelectedOptionalPlatforms] = useState([]);
 const { data: siteSettingsResponse, isLoading: socialPlatformsLoading } = useQuery({
 queryKey: ['siteSettings'],
 queryFn: () => siteSettingsService.get(),
 });
 const socialPlatforms = (() => {
 const list = siteSettingsResponse?.data?.vendor_social_platforms;
 if (Array.isArray(list) && list.length > 0) return list;
 return [
 { key: 'instagram', label: 'Instagram', placeholder: '@yourhandle', is_active: true, is_required: true, sort_order: 1 },
 { key: 'facebook', label: 'Facebook', placeholder: 'page name or @handle', is_active: true, is_required: false, sort_order: 2 },
 { key: 'tiktok', label: 'TikTok', placeholder: '@yourhandle', is_active: true, is_required: false, sort_order: 3 },
 { key: 'x', label: 'X (Twitter)', placeholder: '@yourhandle', is_active: true, is_required: false, sort_order: 4 },
 { key: 'youtube', label: 'YouTube', placeholder: '@channel', is_active: true, is_required: false, sort_order: 5 },
 ];
 })();

 const steps = [
 { id: 1, title: 'Business', description: 'Who you are', icon: Building },
 { id: 2, title: 'Contact', description: 'How clients reach you', icon: MapPin },
 { id: 3, title: 'Documents', description: 'Proof & details', icon: FileText },
 { id: 4, title: 'Review', description: 'Confirm & submit', icon: CheckCircle },
 ];

 const progressPct = Math.round(((currentStep - 1) / (steps.length - 1)) * 100);

 useEffect(() => {
 if (!user || draftRestoredRef.current) return;

 setFormData((prev) => {
 const updated = { ...prev };

 if (user.vendor_profile) {
 // Only seed empty fields so a restored draft is not wiped
 Object.entries(user.vendor_profile).forEach(([key, value]) => {
 if (value == null || value === '') return;
 if (key === 'specialties') {
 if (!Array.isArray(updated.specialties) || updated.specialties.length === 0) {
 updated.specialties = value;
 }
 return;
 }
 if (key === 'social_media' || key === 'business_hours') {
 if (!updated[key] || (typeof updated[key] === 'object' && Object.keys(updated[key]).length === 0)) {
 updated[key] = value;
 }
 return;
 }
 if (updated[key] == null || updated[key] === '') {
 updated[key] = value;
 }
 });
 }

 if (!updated.business_email && user.email) {
 updated.business_email = user.email;
 }
 if (!updated.business_phone && user.phone) {
 updated.business_phone = user.phone;
 }
 if (!updated.city && user.city) {
 updated.city = user.city;
 }
 if (!updated.state && user.state) {
 updated.state = user.state;
 }
 if (!updated.address && user.address) {
 updated.address = user.address;
 }
 if (!updated.zip_code && user.zip_code) {
 updated.zip_code = user.zip_code;
 }

 return updated;
 });
 }, [user]);

 // Restore draft after a session timeout / re-login (runs after profile seed)
 useEffect(() => {
 if (draftRestoredRef.current || isLoading || !user || isVendorVerified || isVerificationPending) {
 return;
 }
 const draft = loadVerificationDraft();
 draftRestoredRef.current = true;
 if (!draft?.formData) return;
 setFormData((prev) => ({
 ...prev,
 ...draft.formData,
 specialties: Array.isArray(draft.formData.specialties)
 ? draft.formData.specialties
 : prev.specialties,
 social_media:
 draft.formData.social_media && typeof draft.formData.social_media === 'object'
 ? draft.formData.social_media
 : prev.social_media,
 business_hours: draft.formData.business_hours || prev.business_hours,
 }));
 if (Array.isArray(draft.selectedOptionalPlatforms)) {
 setSelectedOptionalPlatforms(draft.selectedOptionalPlatforms);
 }
 if (draft.currentStep >= 1 && draft.currentStep <= 4) {
 setCurrentStep(draft.currentStep);
 }
 if (draft.timedOut) {
 info(
 'Draft restored',
 'We restored your verification progress from before your session timed out.'
 );
 saveVerificationDraft({
 formData: draft.formData,
 currentStep: draft.currentStep,
 selectedOptionalPlatforms: draft.selectedOptionalPlatforms,
 timedOut: false,
 });
 }
 }, [user, isLoading, isVendorVerified, isVerificationPending, info]);

 // Autosave draft while the vendor works through the form
 useEffect(() => {
 if (isLoading || isVendorVerified || isVerificationPending || sessionTimedOut) return;
 if (!draftRestoredRef.current) return;
 saveVerificationDraft({
 formData,
 currentStep,
 selectedOptionalPlatforms,
 });
 }, [
 formData,
 currentStep,
 selectedOptionalPlatforms,
 isLoading,
 isVendorVerified,
 isVerificationPending,
 sessionTimedOut,
 ]);

 const handleSessionTimeout = (message) => {
 saveVerificationDraft({
 formData,
 currentStep,
 selectedOptionalPlatforms,
 timedOut: true,
 });
 markVerificationDraftTimedOut();
 setSessionTimedOut(true);
 setIsSubmitting(false);
 showError(
 message ||
 'Your session timed out. Sign in again to finish verification — your progress was saved.'
 );
 };

 const ensureActiveSession = () => {
 if (isSessionExpired()) {
 handleSessionTimeout();
 return false;
 }
 return true;
 };

 // Proactive session check while the vendor is filling the form
 useEffect(() => {
 if (isVendorVerified || isVerificationPending || sessionTimedOut) return undefined;
 const tick = () => {
 if (isSessionExpired()) {
 handleSessionTimeout();
 }
 };
 tick();
 const id = window.setInterval(tick, 15000);
 return () => window.clearInterval(id);
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [isVendorVerified, isVerificationPending, sessionTimedOut]);

 // Do not auto-stamp IP/geo country onto the form — vendor must choose operating country.
 // Geo can be applied explicitly via "Suggest from detected location" on step 2.

 const handleInputChange = (field, value) => {
 setFormData((prev) => ({
 ...prev,
 [field]: value,
 }));

 if (errors[field] || (field === 'country' && errors.zip_code)) {
 setErrors((prev) => ({
 ...prev,
 [field]: null,
 ...(field === 'country' && !countryRequiresPostalCode(value) ? { zip_code: null } : {}),
 }));
 }
 };

 const handleSpecialtyToggle = (specialty) => {
 setFormData((prev) => ({
 ...prev,
 specialties: prev.specialties.includes(specialty)
 ? prev.specialties.filter((s) => s !== specialty)
 : [...prev.specialties, specialty],
 }));
 };

 const handleSocialMediaChange = (platform, value) => {
 setFormData((prev) => ({
 ...prev,
 social_media: {
 ...prev.social_media,
 [platform]: value,
 },
 }));
 };

 const removeSocialMediaPlatform = (platform) => {
 if (platform === 'instagram') return;
 setFormData((prev) => {
 const newSocialMedia = { ...prev.social_media };
 delete newSocialMedia[platform];
 return {
 ...prev,
 social_media: newSocialMedia,
 };
 });
 };

 const toggleOptionalPlatform = (platformKey) => {
 setSelectedOptionalPlatforms((prev) => {
 if (prev.includes(platformKey)) {
 removeSocialMediaPlatform(platformKey);
 return prev.filter((key) => key !== platformKey);
 }
 return [...prev, platformKey];
 });
 };

 const buildSocialMediaPayload = () => {
 const active = socialPlatforms.filter((platform) => platform.is_active !== false);
 const allowedKeys = new Set(active.map((platform) => platform.key));
 allowedKeys.add('instagram');

 const payload = {};
 Object.entries(formData.social_media || {}).forEach(([key, value]) => {
 const handle = String(value || '').trim();
 if (!handle || !allowedKeys.has(key)) return;
 if (
 key !== 'instagram' &&
 !active.some((platform) => platform.key === key && platform.is_required) &&
 !selectedOptionalPlatforms.includes(key)
 ) {
 return;
 }
 payload[key] = handle;
 });
 return payload;
 };

 const toggleDayAvailability = (day) => {
 setFormData((prev) => ({
 ...prev,
 business_hours: {
 ...prev.business_hours,
 [day]: {
 ...prev.business_hours[day],
 available: !prev.business_hours[day].available,
 },
 },
 }));
 };

 const handleFileUpload = async (field, file) => {
 if (!file) return;
 if (!ensureActiveSession()) return;

 const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
 const maxSize = 10 * 1024 * 1024;

 if (!allowedTypes.includes(file.type)) {
 setErrors((prev) => ({
 ...prev,
 [field]: 'Please upload a valid image (JPEG, PNG) or PDF file',
 }));
 return;
 }

 if (file.size > maxSize) {
 setErrors((prev) => ({
 ...prev,
 [field]: 'File size must be less than 10MB',
 }));
 return;
 }

 setUploadProgress((prev) => ({ ...prev, [field]: 0 }));

 try {
 const uploadResult = await verificationService.uploadDocument(file, field, (progress) => {
 setUploadProgress((prev) => ({ ...prev, [field]: progress }));
 });

 setFormData((prev) => ({
 ...prev,
 [field]: {
 file: file,
 name: file.name,
 url: uploadResult.data.url,
 filename: uploadResult.data.filename,
 size: uploadResult.data.size,
 mime_type: uploadResult.data.mime_type,
 },
 }));

 setErrors((prev) => ({
 ...prev,
 [field]: null,
 }));

 setUploadProgress((prev) => ({ ...prev, [field]: null }));
 } catch (error) {
 console.error('File upload error:', error);
 setUploadProgress((prev) => ({ ...prev, [field]: null }));
 if (isAuthError(error) || isSessionExpired()) {
 handleSessionTimeout(
 'Your session timed out while uploading. Sign in again — your progress was saved.'
 );
 return;
 }
 setErrors((prev) => ({
 ...prev,
 [field]: error.response?.data?.message || 'Failed to upload file. Please try again.',
 }));
 }
 };

 const validateStep = (step) => {
 const newErrors = {};

 switch (step) {
 case 1:
 if (!formData.business_name.trim()) newErrors.business_name = 'Business name is required';
 if (!formData.service_categories)
 newErrors.service_categories = 'Please select a service category';
 if (!formData.business_description.trim())
 newErrors.business_description = 'Business description is required';
 if (formData.business_description.length > 500)
 newErrors.business_description = 'Business description must not exceed 500 characters';
 if (!formData.years_experience)
 newErrors.years_experience = 'Years of experience is required';
 if (formData.specialties.length === 0)
 newErrors.specialties = 'Please select at least one specialty';
 break;
 case 2:
 if (!formData.business_phone.trim()) newErrors.business_phone = 'Business phone is required';
 if (!formData.business_email.trim()) newErrors.business_email = 'Business email is required';
 if (!formData.address.trim()) newErrors.address = 'Business address is required';
 if (!formData.city.trim()) newErrors.city = 'City is required';
 if (!formData.state.trim()) newErrors.state = 'State is required';
 if (countryRequiresPostalCode(formData.country) && !formData.zip_code.trim()) {
 newErrors.zip_code = 'ZIP code is required';
 }
 if (marketsLoading) {
 newErrors.country = 'Markets are still loading';
 } else if (!isConfiguredMarket(markets, formData.country)) {
 newErrors.country = markets.length
 ? 'Select an operating market'
 : 'No operating markets are set up yet';
 }
 {
 const instagramHandle = String(formData.social_media?.instagram || '').trim();
 if (!instagramHandle) {
 newErrors.instagram = 'Instagram handle is required';
 }
 socialPlatforms
 .filter((platform) => platform.is_active !== false && platform.is_required && platform.key !== 'instagram')
 .forEach((platform) => {
 if (!String(formData.social_media?.[platform.key] || '').trim()) {
 newErrors[`social_${platform.key}`] = `${platform.label} handle is required`;
 }
 });
 selectedOptionalPlatforms.forEach((key) => {
 const platform = socialPlatforms.find((item) => item.key === key);
 if (!String(formData.social_media?.[key] || '').trim()) {
 newErrors[`social_${key}`] = `${platform?.label || key} handle is required`;
 }
 });
 }
 break;
 case 3:
 if (!formData.business_license?.url) {
 newErrors.business_license = 'Business license is required';
 }
 if (!formData.insurance_certificate?.url) {
 newErrors.insurance_certificate = 'Insurance certificate is required';
 }
 if (!formData.id_document?.url) {
 newErrors.id_document = 'ID document is required';
 }
 break;
 default:
 break;
 }

 setErrors(newErrors);
 return Object.keys(newErrors).length === 0;
 };

 const handleNext = () => {
 if (!ensureActiveSession()) return;
 if (validateStep(currentStep)) {
 setCurrentStep((prev) => prev + 1);
 window.scrollTo({ top: 0, behavior: 'smooth' });
 }
 };

 const handlePrevious = () => {
 setCurrentStep((prev) => prev - 1);
 window.scrollTo({ top: 0, behavior: 'smooth' });
 };

 const handleSubmit = async () => {
 if (!ensureActiveSession()) return;

 if (!validateStep(3)) {
 setCurrentStep(3);
 window.scrollTo({ top: 0, behavior: 'smooth' });
 return;
 }

 setIsSubmitting(true);
 justSubmittedRef.current = true;
 hasNavigatedRef.current = false;

 try {
 const verificationData = {
 user_id: user.id,
 verification_type: 'vendor_business',
 verification_status: 'pending',
 verification_data: {
 business_name: formData.business_name?.trim(),
 service_categories: formData.service_categories ? [formData.service_categories] : [],
 business_description: formData.business_description?.trim(),
 years_experience: formData.years_experience,
 specialties: formData.specialties || [],
 business_phone: formData.business_phone?.trim(),
 business_email: formData.business_email?.trim(),
 website: formData.website?.trim() || null,
 social_media: buildSocialMediaPayload(),
 address: formData.address?.trim(),
 city: formData.city?.trim(),
 state: formData.state?.trim(),
 zip_code: formData.zip_code?.trim(),
 country: formData.country,
 insurance_info: formData.insurance_info?.trim(),
 business_hours: formData.business_hours,
 team_size: formData.team_size?.trim(),
 certifications: formData.certifications?.trim(),
 references: formData.references?.trim(),
 },
 verification_document_url: {
 business_license: formData.business_license?.url || null,
 insurance_certificate: formData.insurance_certificate?.url || null,
 id_document: formData.id_document?.url || null,
 },
 };

 const vendorProfileData = {
 business_name: formData.business_name?.trim(),
 description: formData.business_description?.trim(),
 address: formData.address?.trim(),
 city: formData.city?.trim(),
 state: formData.state?.trim(),
 zip_code: formData.zip_code?.trim(),
 country: formData.country,
 website: formData.website?.trim() || null,
 business_phone: formData.business_phone?.trim() || null,
 social_media: buildSocialMediaPayload(),
 years_experience: parseInt(formData.years_experience) || 0,
 specialties: formData.specialties || [],
 insurance_info: formData.insurance_info?.trim(),
 business_hours: formData.business_hours,
 team_size: parseInt(formData.team_size) || null,
 certifications: formData.certifications?.trim() || null,
 references: formData.references?.trim() || null,
 service_categories: formData.service_categories ? [formData.service_categories] : [],
 is_verified: true,
 };

 // Seed payout/tax row from business + contact. Stripe Connect collects
 // identity / tax IDs later on the payouts page — vendors skip that here.
 const taxCountry = String(formData.country || '').toUpperCase();
 const taxProfileData = {
 legal_name: formData.business_name?.trim() || null,
 business_name: formData.business_name?.trim() || null,
 tax_residence_country: taxCountry || null,
 region_state: formData.state?.trim() || null,
 city: formData.city?.trim() || null,
 address_line1: formData.address?.trim() || null,
 postal_code: formData.zip_code?.trim() || null,
 payout_country: taxCountry || null,
 payout_currency: 'USD',
 mark_complete: false,
 };

 await verificationService.submitVerificationWithProfile({
 verification_data: verificationData.verification_data,
 verification_document_url: verificationData.verification_document_url,
 vendor_profile_data: vendorProfileData,
 tax_profile: taxProfileData,
 });

 clearVerificationDraft();
 success('Verification completed successfully! Your vendor account has been verified.');

 await refreshVerificationStatus();

 setTimeout(() => {
 if (!hasNavigatedRef.current) {
 hasNavigatedRef.current = true;
 navigate('/vendor');
 }
 justSubmittedRef.current = false;
 }, 300);
 } catch (error) {
 console.error('Verification submission error:', error);
 justSubmittedRef.current = false;

 if (isAuthError(error) || isSessionExpired()) {
 handleSessionTimeout(
 'Your session timed out before we could submit. Sign in again — your progress was saved.'
 );
 return;
 }

 const status = error.response?.status;
 const apiMessage = error.response?.data?.message;
 const validationErrors = error.response?.data?.errors;
 let errorMessage =
 apiMessage || 'Failed to submit verification. Please try again.';

 if (status === 422 && validationErrors && typeof validationErrors === 'object') {
 const firstKey = Object.keys(validationErrors)[0];
 const firstMsg = firstKey ? validationErrors[firstKey]?.[0] : null;
 if (firstMsg) errorMessage = firstMsg;
 }

 showError(errorMessage);
 } finally {
 setIsSubmitting(false);
 }
 };

 const renderCurrentStep = () => {
 if (currentStep === 1 && categoriesLoading) {
 return (
 <div className="space-y-5 animate-pulse">
 {[1, 2, 3, 4].map((i) => (
 <div key={i} className="space-y-2">
 <div className="h-3 w-28 rounded bg-gray-200" />
 <div className="h-11 w-full rounded-xl bg-gray-200" />
 </div>
 ))}
 </div>
 );
 }

 if (currentStep === 2 && marketsLoading) {
 return (
 <div className="space-y-5 animate-pulse">
 {[1, 2, 3, 4, 5, 6].map((i) => (
 <div key={i} className="space-y-2">
 <div className="h-3 w-28 rounded bg-gray-200" />
 <div className="h-11 w-full rounded-xl bg-gray-200" />
 </div>
 ))}
 </div>
 );
 }

 switch (currentStep) {
 case 1:
 return (
 <Step1BusinessInfo
 formData={formData}
 errors={errors}
 serviceCategories={serviceCategories}
 categoriesLoading={categoriesLoading}
 categoriesError={categoriesError}
 handleInputChange={handleInputChange}
 handleSpecialtyToggle={handleSpecialtyToggle}
 />
 );
 case 2:
 return (
 <Step2ContactLocation
 formData={formData}
 errors={errors}
 markets={markets}
 marketOptions={marketOptions}
 marketsLoading={marketsLoading}
 marketsError={marketsError}
 geoHint={{
 country: geoCountry,
 countryCode: geoCountryCode,
 city: geoCity,
 state: geoState,
 loading: geoLoading,
 }}
 onSuggestFromGeo={async () => {
 const detected = await getCurrentLocation?.({ preferIp: true });
 if (!detected) {
 showError('Could not detect a location hint. Select your country manually.');
 return;
 }
 const code = (detected.countryCode || '').toUpperCase();
 const detectedIsMarket = isConfiguredMarket(markets, code);
 setFormData((prev) => ({
 ...prev,
 country: detectedIsMarket ? code : prev.country,
 city: detected.city || prev.city,
 state: detected.state || prev.state,
 }));
 info(
 detectedIsMarket ? 'Suggested from current connection' : 'Detected country is not a market',
 detectedIsMarket
 ? 'Review the market before continuing. This does not change your storefront until you submit.'
 : 'City was filled in. Choose an operating market from the list.'
 );
 }}
 handleInputChange={handleInputChange}
 handleSocialMediaChange={handleSocialMediaChange}
 removeSocialMediaPlatform={removeSocialMediaPlatform}
 socialPlatforms={socialPlatforms}
 socialPlatformsLoading={socialPlatformsLoading}
 selectedOptionalPlatforms={selectedOptionalPlatforms}
 onToggleOptionalPlatform={toggleOptionalPlatform}
 />
 );
 case 3:
 return (
 <Step3Documents
 formData={formData}
 errors={errors}
 uploadProgress={uploadProgress}
 handleFileUpload={handleFileUpload}
 handleInputChange={handleInputChange}
 toggleDayAvailability={toggleDayAvailability}
 />
 );
 case 4:
 return (
 <Step4Review
 formData={formData}
 serviceCategories={serviceCategories}
 marketOptions={marketOptions}
 />
 );
 default:
 return null;
 }
 };

 if (isLoading) {
 return <VerificationSkeleton />;
 }

 if (sessionTimedOut) {
 return (
 <StatusCard
 icon={Clock}
 iconClass="border-amber-200 bg-amber-50 text-amber-700"
 title="Session timed out"
 body="Your sign-in expired before verification could finish. Your progress was saved — sign in again to continue where you left off."
 action={
 <button
 type="button"
 onClick={async () => {
 saveVerificationDraft({
 formData,
 currentStep,
 selectedOptionalPlatforms,
 timedOut: true,
 });
 try {
 await logout();
 } catch {
 // ignore
 }
 navigate('/login?session=expired&next=/vendor/verification', {
 replace: true,
 });
 }}
 className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl bg-gray-900 px-5 py-3 text-sm font-semibold text-white hover:bg-black"
 >
 <LogIn className="h-4 w-4" />
 Sign in to continue
 </button>
 }
 />
 );
 }

 if (isVendorVerified) {
 return (
 <StatusCard
 icon={ShieldCheck}
 iconClass="border-emerald-200 bg-emerald-50 text-emerald-600"
 title="Already verified"
 body="You have already completed the verification process."
 footer="Redirecting to dashboard…"
 />
 );
 }

 if (isVerificationPending) {
 return (
 <StatusCard
 icon={Clock}
 iconClass="border-amber-200 bg-amber-50 text-amber-600"
 title="Verification pending"
 body="Your verification is under review. You’ll get an email once it’s complete."
 footer="Redirecting to dashboard…"
 />
 );
 }

 const activeStep = steps.find((s) => s.id === currentStep) || steps[0];

 return (
 <VerificationShell wide>
 <div className="pb-8">
 <h1 className="text-center text-2xl font-semibold text-gray-900">
 Verify your business
 </h1>
 <p className="mt-1 text-center text-sm text-gray-500">
 Step {currentStep} of {steps.length} · {activeStep.description}
 </p>

 {/* Compact stepper */}
 <nav aria-label="Verification steps" className="mt-8 mb-8">
 <ol className="flex items-start justify-between gap-0.5">
 {steps.map((step, index) => {
 const StepIcon = step.icon;
 const isActive = currentStep === step.id;
 const isCompleted = currentStep > step.id;
 const isLast = index === steps.length - 1;

 return (
 <li key={step.id} className="flex-1 min-w-0 flex items-start">
 <div className="flex flex-col items-center w-full min-w-0">
 <div className="flex items-center w-full">
 {index > 0 ? (
 <div
 className={`h-0.5 flex-1 rounded-full transition-colors ${ isCompleted || isActive ? 'bg-gray-900' : 'bg-gray-200' }`}
 aria-hidden
 />
 ) : (
 <div className="flex-1" aria-hidden />
 )}
 <div
 className={`relative z-[1] flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-300 ${ isCompleted ? 'border-gray-900 bg-gray-900 text-white' : isActive ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 bg-surface text-gray-400' }`}
 aria-current={isActive ? 'step' : undefined}
 >
 {isCompleted ? (
 <CheckCircle className="h-4 w-4" />
 ) : (
 <StepIcon className="h-3.5 w-3.5" />
 )}
 </div>
 {!isLast ? (
 <div
 className={`h-0.5 flex-1 rounded-full transition-colors ${ isCompleted ? 'bg-gray-900' : 'bg-gray-200' }`}
 aria-hidden
 />
 ) : (
 <div className="flex-1" aria-hidden />
 )}
 </div>
 <p
 className={`mt-2 text-[11px] font-medium tracking-wide truncate max-w-full px-0.5 text-center ${ isActive || isCompleted ? 'text-gray-900' : 'text-gray-400' }`}
 >
 {step.title}
 </p>
 </div>
 </li>
 );
 })}
 </ol>

 <div className="mt-4 h-1 rounded-full bg-gray-100 overflow-hidden">
 <div
 className="h-full rounded-full bg-gray-900 transition-all duration-500 ease-out"
 style={{ width: `${progressPct}%` }}
 />
 </div>
 </nav>

 <div className="space-y-1">{renderCurrentStep()}</div>

 <div className="mt-8 flex items-center justify-between gap-3">
 <Button
 onClick={handlePrevious}
 variant="outline"
 disabled={currentStep === 1 || isSubmitting}
 className="rounded-md min-w-[6.5rem]"
 >
 Back
 </Button>

 {currentStep < steps.length ? (
 <Button onClick={handleNext} className="rounded-md min-w-[7.5rem]">
 Continue
 </Button>
 ) : (
 <Button
 onClick={handleSubmit}
 disabled={isSubmitting}
 className="rounded-md inline-flex items-center gap-2"
 >
 {isSubmitting ? (
 <>
 <Loader2 className="w-4 h-4 animate-spin" />
 Submitting…
 </>
 ) : (
 <>
 <ShieldCheck className="w-4 h-4" />
 Submit verification
 </>
 )}
 </Button>
 )}
 </div>

 <p className="mt-6 text-center text-sm text-gray-500">
 <button
 type="button"
 onClick={() => navigate('/vendor')}
 className="font-medium text-gray-900 underline-offset-2 hover:underline"
 >
 Back to dashboard
 </button>
 </p>
 </div>
 </VerificationShell>
 );
};

export default VendorVerification;
